import { pxToNumber } from '@billy/design-system'
import React, { ReactElement, useCallback, useEffect, useState } from 'react'

import { getImageClassName } from '../../utils/getClassName'
import { getInitials } from '../../utils/getInitials'
import { sizeToPx } from './constants/sizeToPx'
import * as Styled from './styles'
import { Size } from './types/size'

export interface AvatarProps {
  backgroundColor?: string
  initials?: string
  name?: string
  noMargin?: boolean
  size?: Size
  url?: string
}

export const Avatar = ({ backgroundColor, initials, name, noMargin, size = 'm', url }: AvatarProps): ReactElement => {
  const [hasAvatar, setHasAvatar] = useState(!!url)
  const [isLoading, setIsLoading] = useState(hasAvatar)

  useEffect(() => {
    setHasAvatar(!!url)
    setIsLoading(!!url)
  }, [url])

  const avatarSizeValue = pxToNumber(sizeToPx[size])

  const handleError = useCallback(() => {
    setHasAvatar(false)
    setIsLoading(false)
  }, [])

  const handleLoad = useCallback(() => {
    setIsLoading(false)
  }, [])

  const renderContent = () => {
    if (hasAvatar) {
      return <Styled.Avatar isLoading={isLoading} onError={handleError} onLoad={handleLoad} src={url} />
    }

    const resolvedInitials = initials ?? (name ? getInitials(name) : undefined)

    if (resolvedInitials) {
      return (
        <Styled.AvatarInitials backgroundColor={backgroundColor} size={size}>
          {resolvedInitials}
        </Styled.AvatarInitials>
      )
    }

    return <Styled.AvatarPlaceholder height={avatarSizeValue} width={avatarSizeValue} />
  }

  return (
    <Styled.AvatarWrapper className={getImageClassName()} noMargin={noMargin} size={size}>
      {isLoading && <Styled.SkeletonAvatar size={avatarSizeValue} />}
      {renderContent()}
    </Styled.AvatarWrapper>
  )
}
